import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer, FilePdf } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { LabelSheet } from "@/components/LabelSheet";
import { SlabLabel } from "@/components/SlabLabel";
import { RemnantLabel } from "@/components/RemnantLabel";
import { api } from "@/lib/api";

const CONFIG = {
  jobs: {
    endpoint: "reservations",
    back: "/inventory/material-on-hold",
    title: "Material Labels",
  },
  slabs: { endpoint: "slabs", back: "/inventory/slabs", title: "Slab Labels" },
  remnants: {
    endpoint: "remnants",
    back: "/inventory/remnants",
    title: "Remnant Labels",
  },
};

export default function BatchPrint() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const type = params.get("type") || "jobs";
  const cfg = CONFIG[type] || CONFIG.jobs;
  const [items, setItems] = useState([]);

  useEffect(() => {
    const ids = (params.get("ids") || "").split(",").filter(Boolean);
    Promise.all(
      ids.map((id) =>
        api
          .get(`/${cfg.endpoint}/${id}`)
          .then((r) => r.data)
          .catch(() => null),
      ),
    ).then((res) => setItems(res.filter(Boolean)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const downloadPdf = async () => {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import("jspdf"),
      import("html2canvas"),
    ]);
    const pdf = new jsPDF({
      unit: "in",
      format: [4, 6],
      orientation: "portrait",
    });
    const nodes = document.querySelectorAll("#print-area .print-label");
    for (let i = 0; i < nodes.length; i += 1) {
      const canvas = await html2canvas(nodes[i], {
        scale: 2,
        backgroundColor: "#ffffff",
      });
      if (i > 0) pdf.addPage([4, 6], "portrait");
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, 4, 6);
    }
    pdf.save(`${type}-labels-${nodes.length}.pdf`);
    toast.success(`${nodes.length} labels saved to PDF`);
  };

  const renderLabel = (item) => {
    if (type === "slabs") return <SlabLabel s={item} />;
    if (type === "remnants") return <RemnantLabel r={item} />;
    return <LabelSheet r={item} />;
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Batch labels" />
      <main className="relative z-10 mx-auto max-w-6xl px-4 pb-20 pt-6">
        <button
          onClick={() => navigate(cfg.back)}
          data-testid="back-button"
          className="no-print mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <div className="no-print flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              {cfg.title}
            </h1>
            <p className="mt-1 text-sm text-zinc-500" data-testid="batch-count">
              {items.length} label{items.length === 1 ? "" : "s"} queued — one
              4x6 page each
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="batch-print-button"
              onClick={() => window.print()}
              disabled={!items.length}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
            >
              <Printer size={18} weight="bold" className="mr-2" /> Print All
            </Button>
            <Button
              data-testid="batch-pdf-button"
              variant="outline"
              onClick={downloadPdf}
              disabled={!items.length}
              className="h-12 border-zinc-700 bg-transparent font-semibold uppercase tracking-wider hover:bg-zinc-800"
            >
              <FilePdf size={18} weight="bold" className="mr-2" /> PDF
            </Button>
          </div>
        </div>

        <div
          id="print-area"
          data-testid="batch-label-area"
          className="mt-8 flex flex-wrap gap-6"
        >
          {items.map((item) => (
            <div
              key={item.id}
              className="origin-top-left scale-[0.8] sm:scale-100"
              style={{ width: "4in" }}
            >
              {renderLabel(item)}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
